import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { FormInstance } from "antd";
import DocumentInfo from "components/documentInfo/DocumentInfo";
import RenderTable from "components/table";
import { bindActionCreators } from "redux";
import { Creators as AttendanceActions } from "store/attendance/attendance-duck";
import { IAttendanceItems } from "store/attendance/attendance-model";
import { Creators as LobbyActions } from "store/lobby/lobby-duck";
import { Creators as UserSessionActions } from "store/user-session/user-session-duck";
import { IUserData } from "store/user-session/user-session-model";
import { readConfigFile } from "utils/appConfig";
import ColumnsTable from "./columnsTable";
import * as S from "./styled/index.styled";

interface IPropsScreenAttendanceGrid {
  items?: Array<IAttendanceItems>;
  totalItems?: number;
  itemsByPage?: number;
  pageIndex?: number;
  userData: IUserData;
  isLoadingTable: boolean;
  allowRequestDockEntryAction: boolean;
  allowRequestVehicleInboundAction: boolean;
  hasScheduling: boolean;
  allowRequestVehicleEntryAuthorizationAction: boolean;
  formVehicleActionAttendance: FormInstance | any;
  setGateEventId?: React.Dispatch<any>;
  setYardRegisterId: React.Dispatch<any>;
  setVehicleActionSelected?: React.Dispatch<any>;
  setVehiclePlateSelected: React.Dispatch<any>;
  hasRfidReadingActive?: boolean;
}
interface IDispatchProps {
  lobbyFetchItemCheckinAsync: (...args) => void;
  lobbyFetchItemAsync: (...args) => void;
  attendanceFetchRegisterAsync: (...args) => void;
  attendanceChangeFieldValue: (...args) => void;
  attendanceShowDrawerViewCheckinRegister: (...args) => void;
  attendanceShowDrawerViewRegister: (...args) => void;
  attendanceShowModalVehicleAction: (...args) => void;
  attendanceShowModalReleaseVehicleAction: (...args) => void;
  attendanceShowDrawerVehicleEntryAuthorization: (...args) => void;
  attendanceFetchItemVehicleEntryAuthorizationAsync: (...args) => void;
}

export type IProps = IPropsScreenAttendanceGrid & IDispatchProps;

const AttendanceGrid = ({
  items,
  totalItems,
  pageIndex,
  userData,
  allowRequestDockEntryAction,
  allowRequestVehicleInboundAction,
  allowRequestVehicleEntryAuthorizationAction,
  formVehicleActionAttendance,
  isLoadingTable,
  hasScheduling,
  hasRfidReadingActive,
  attendanceFetchRegisterAsync,
  attendanceChangeFieldValue,
  lobbyFetchItemCheckinAsync,
  lobbyFetchItemAsync,
  attendanceShowDrawerViewCheckinRegister,
  attendanceShowDrawerViewRegister,
  attendanceShowModalVehicleAction,
  attendanceShowModalReleaseVehicleAction,
  attendanceShowDrawerVehicleEntryAuthorization,
  attendanceFetchItemVehicleEntryAuthorizationAsync,
  setGateEventId,
  setYardRegisterId,
  setVehiclePlateSelected,
  setVehicleActionSelected,
}: IProps) => {
  const [isEnabledProcessId, setIsEnabledProcessId] = useState([]);
  const fetchAttendance = useCallback(() => attendanceFetchRegisterAsync(), [attendanceFetchRegisterAsync]);

  const resetAttendanceChangeFieldValue = () => {
    attendanceChangeFieldValue("pageIndex", 1);
    attendanceChangeFieldValue("itemsByPage", 10);
  };

  useEffect(() => {
    const loadConfig = async () => {
      const config = await readConfigFile();
      const allowedProcessId = config.VITE_VEHICLE_RELEASE_ALLOWED_PROCESS_ID ? JSON.parse(config.VITE_VEHICLE_RELEASE_ALLOWED_PROCESS_ID) : [];
      setIsEnabledProcessId(allowedProcessId);
    };

    loadConfig();
  }, []);

  useEffect(() => {
    resetAttendanceChangeFieldValue();
    fetchAttendance();
  }, [fetchAttendance]);

  const checkIfIsFeaturedOrInLocation = (
    eventType,
    id,
    lobbyFetchItemCheckinAsync,
    lobbyFetchItemAsync,
    attendanceShowDrawerViewCheckinRegister,
    attendanceShowDrawerViewRegister
  ) => {
    const isCheckinOrInRadiusData = [0, 4];
    if (isCheckinOrInRadiusData.includes(eventType)) {
      lobbyFetchItemCheckinAsync(id);
      attendanceShowDrawerViewCheckinRegister();
    } else {
      lobbyFetchItemAsync(id);
      attendanceShowDrawerViewRegister();
    }
  };

  const handleClickView = ({ id, local }) => {
    checkIfIsFeaturedOrInLocation(local, id, lobbyFetchItemCheckinAsync, lobbyFetchItemAsync, attendanceShowDrawerViewCheckinRegister, attendanceShowDrawerViewRegister);
  };

  const handleClickVehicleAction = ({ gateEventId }) => {
    attendanceShowModalVehicleAction();
    setGateEventId(gateEventId);
    formVehicleActionAttendance.setFieldsValue({ dockAttendance: "" });
    setVehicleActionSelected(1);
  };

  const handleClickVehicleEntryAuthorization = ({ id }) => {
    attendanceShowDrawerVehicleEntryAuthorization();
    attendanceFetchItemVehicleEntryAuthorizationAsync({ id });
  };

  const handleTableChange = (pagination, sorter, defaultSortOrder) => {
    attendanceChangeFieldValue("orderBy", defaultSortOrder.order);
    attendanceChangeFieldValue("itemsByPage", pagination.pageSize);
    attendanceChangeFieldValue("pageIndex", pagination.current);
    fetchAttendance();
  };
  const handleClickReleaseVehicleAction = ({ id, vehiclePlate }) => {
    attendanceShowModalReleaseVehicleAction();
    setVehiclePlateSelected(vehiclePlate);
    setYardRegisterId(id);
  };

  const handleClickViewDocument = ({ documents, vehiclePlate }) => {
    return <DocumentInfo documents={documents} vehiclePlate={vehiclePlate} />;
  };

  const handlesClick = {
    handleClickView,
    handleClickVehicleAction,
    handleClickReleaseVehicleAction,
    handleClickVehicleEntryAuthorization,
    handleClickViewDocument,
  };

  return (
    <S.AttendanceContainer>
      <RenderTable
        columnsTable={ColumnsTable({
          userData,
          handlesClick,
          allowRequestVehicleInboundAction,
          allowRequestDockEntryAction,
          allowRequestVehicleEntryAuthorizationAction,
          hasScheduling,
          isEnabledProcessId,
          hasRfidReadingActive,
        })}
        items={items}
        totalItems={totalItems}
        handleTableChange={handleTableChange}
        pageIndex={pageIndex}
        isLoadingTable={isLoadingTable}
      />
    </S.AttendanceContainer>
  );
};

const mapDispatchToActions = (dispatch: any) => bindActionCreators({ ...UserSessionActions, ...LobbyActions, ...AttendanceActions }, dispatch);

const mapStateToProps = (state) => ({
  items: state.Attendance.items,
  totalItems: state.Attendance.totalItems,
  itemsByPage: state.Attendance.filter.itemsByPage,
  pageIndex: state.Attendance.filter.pageIndex,
  userData: state.UserSession.userData,
  allowRequestDockEntryAction: state.Lobby.allowRequestDockEntryAction,
  allowRequestVehicleInboundAction: state.Lobby.allowRequestVehicleInboundAction,
  allowRequestVehicleEntryAuthorizationAction: state.Lobby.allowRequestVehicleEntryAuthorizationAction,
  hasScheduling: state.Lobby.hasScheduling,
  isLoadingTable: state.Attendance.isLoadingTable,
  hasRfidReadingActive: state.Lobby.hasRfidReading,
});

export default connect(mapStateToProps, mapDispatchToActions)(AttendanceGrid);
